import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output, ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {DecimalPipe} from "@angular/common";
import {CcBaseComponent} from "../../../../crystal-elements/cc-base.component";
import {FormFieldGroupConfig} from "../../../../model/api/form-field-group-config";
import {ReportProfileService} from "../../../../services/archive/report-profile.service";
import {FieldRecord} from "../../../../model/api/field-record";
import {ReportProfile} from "../../../../model/archive/report-profile";
import {ProfileAbsService} from "../../../../services/archive/profile-abs.service";
import {FormFieldConfig} from "../../../../model/api/form-field-config";
import {CommonFieldService} from "../../../../services/common/common-field.service";
import {BatchUpdateRequest} from "../../../../model/api/batch-update-request";
import TYPE = FormFieldConfig.TYPE;
import VISIBILITY_RULE = ReportProfile.VISIBILITY_RULE;
import {CcFormDialogComponent} from "../../../../crystal-elements/form-dialog/cc-form-dialog.component";
import {RecapProfileService} from "../../../../services/archive/recap-profile.service";
import {ReportTask} from "../../../../model/archive/report-task";
import {BatchUtils} from "../../../../utils/batch-utils";

@Component({
  selector: 'app-batch-update-profile-form',
  templateUrl: './batch-update-profile-form.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DecimalPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchUpdateProfileFormComponent extends CcBaseComponent {


  formLoading: boolean = false;

  formSubmitted: boolean = true;

  formConfig: FormFieldGroupConfig[] = [];

  dialogFormTitle: string = '';

  @Input()
  visibilities: FieldRecord<any>[] = [];
  @Input()
  exportFormats: FieldRecord<any>[] = [];
  @Input()
  exportDateModes: FieldRecord<any>[] = [];
  @Input()
  executionTimesValues: FieldRecord<any>[] = [];
  @Input()
  executionDaysOfMonthValues: FieldRecord<any>[] = [];
  @Input()
  transferMailTemplates: FieldRecord<any>[] = [];
  @Input()
  transferFileLocationsValues: FieldRecord<any>[] = [];
  @Input()
  profileTypes: FieldRecord<any>[] = [];
  @Input()
  userGroups: FieldRecord<any>[];

  @Input()
  formRecord: ReportProfile;
  @Input()
  transPartKey: string;
  @Input()
  selectedRecords: ReportProfile[] = [];
  @Input()
  dialogFormVisible: boolean = false;
  @Output()
  dialogFormVisibleChange = new EventEmitter<boolean>();

  @Output()
  onSavedForm = new EventEmitter<any>();

  @ViewChild('batchUpdateFormDialog')
  ccFormDialog: CcFormDialogComponent;

  @Input()
  origin: ReportTask.Origin;

  fieldsToUpdate: string[] = [];

  constructor(cdref: ChangeDetectorRef,
              protected reportProfileService: ReportProfileService,
              protected recapProfileService: RecapProfileService,
              protected commonFieldService: CommonFieldService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.initFormConfigs();
    this.dialogFormTitle = this.getTranslation('page.' + this.transPartKey + '.table.batch_update_button');
  }


  protected initFormConfigs() {

    let simplified = !this.isAdmin();
    this.formConfig = [];
    const groupFields = [];
    if (!simplified) {
      groupFields.push({
          field: 'active',
          column: 3,
          type: TYPE.CHECKBOX,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('active')
        },
        {
          field: 'visibility',
          column: 3,
          type: TYPE.DROPDOWN,
          options: () => this.visibilities,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('visibility')
        },);
    }
    groupFields.push(
      {
        field: 'type',
        column: 3,
        type: TYPE.AUTOCOMPLETE,
        filter: true,
        options: (record) => this.profileTypes,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('type')
      },);

    if (!simplified) {
      groupFields.push(
        {
          field: 'userGroup',
          filter: true,
          column: 3,
          type: TYPE.DROPDOWN,
          options: () => this.userGroups,
          hidden: (record: ReportProfile) => record.visibility != VISIBILITY_RULE.USER_GROUP,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('userGroup')
        },
        {
          column: 3,
          type: TYPE.NONE,
          hidden: (record: ReportProfile) => record.visibility == VISIBILITY_RULE.USER_GROUP
        },);
    }
    groupFields.push(
      {
        column: 0,
        type: TYPE.NONE,
        validate: (record: ReportProfile) => this.validate()
      }
    );
    this.formConfig.push({
      group: '',
      fields: groupFields
    });
    const reportGroupFields: FormFieldConfig[] = [
      {
        field: 'exportFormat',
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.exportFormats,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('exportFormat')
      },
      {
        field: 'zipFile',
        column: 3,
        type: TYPE.CHECKBOX,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('zipFile')
      },
      {
        field: 'ignoreEmpty',
        column: 3,
        type: TYPE.CHECKBOX,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('ignoreEmpty')
      },
      {
        type: TYPE.NONE,
        column: 3,
      },
    ];
    if (!simplified) {
      this.formConfig.push({
        group: 'schedule',
        fields:
          [{
            field: 'executionTimesJS',
            column: 3,
            type: TYPE.MULTISELECT,
            options: () => this.executionTimesValues,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, "executionTimes", event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('executionTimes')
          },
            {
              field: 'executionDaysOfMonthJS',
              column: 3,
              type: TYPE.MULTISELECT,
              options: () => this.executionDaysOfMonthValues,
              labelCheckbox: true,
              labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, "executionDaysOfMonth", event2.checked),
              disabled: (record: any) => !this.fieldsToUpdate.includes('executionDaysOfMonth')
            },
            {
              field: 'executionCron',
              column: 3,
              type: TYPE.CRON,
              labelCheckbox: true,
              labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
              disabled: (record: any) => !this.fieldsToUpdate.includes('executionCron')
            }]
      })
    }
    const transferGroupFields = [
      {
        field: 'transferMailTemplate',
        column: 4,
        type: TYPE.DROPDOWN,
        options: () => this.transferMailTemplates,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('transferMailTemplate')
      }, {
        field: 'transferMailSubject',
        column: 8,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('transferMailSubject')
      },
      {
        field: 'transferMailRecipients',
        column: 12,
        type: TYPE.MULTITEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('transferMailRecipients')
      }];
    if (!simplified) {
      transferGroupFields.push(
        {
          field: 'transferFileLocations',
          column: 6,
          type: TYPE.MULTISELECT,
          options: () => this.transferFileLocationsValues,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) =>BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('transferFileLocations')
        }
      );
    }
    this.formConfig.push({
      group: 'transfer',
      fields:
      transferGroupFields
    });
    if (!simplified) {
      this.formConfig.push({
        group: 'alert',
        fields:
          [{
            field: 'mailAlert',
            column: 3,
            type: TYPE.CHECKBOX,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('mailAlert')
          }]
      });
    }
  }

  closeDialogForm(): void {
    this.dialogFormVisible = false;
    this.dialogFormVisibleChange.emit(false);
    this.markForCheck();
  }

  save() {
    this.formLoading = true;
    const batchUpdateRequest = new BatchUpdateRequest<ReportProfile>();
    batchUpdateRequest.recordToApply = {...this.formRecord}
    batchUpdateRequest.recordIds = this.selectedRecords.map(value => value.name);
    batchUpdateRequest.fieldToUpdate = [...this.fieldsToUpdate];

    const service: ProfileAbsService = this.origin == ReportTask.Origin.WEB_REPORT ? this.reportProfileService : this.recapProfileService;
    service.saveBatch(batchUpdateRequest).subscribe(validation => {
      if (validation.success) {
        this.fieldsToUpdate.splice(0)
        this.onSavedForm.emit(this.formRecord);
        this.closeDialogForm();
      } else {
        this.errorMessage(validation.message);
      }
      this.formLoading = false;
    });
  }

  dialogFormVisibleChanged($event: boolean) {
    this.dialogFormVisibleChange.emit($event);
    this.fieldsToUpdate.splice(0);
  }

  validate(): boolean {
    return this.fieldsToUpdate.length == 0;
  }
}
